import React from 'react';
import { GatsbyBrowser } from 'gatsby';
import { HelmetProvider } from 'react-helmet-async';
import { CacheProvider } from '@emotion/react';
import createEmotionCache from './src/utils/create-emotion-cache';

interface ExtendedWindow extends Window {
    gatsby_router_hash?: string;
    dataLayer?: unknown[];
}

const scrollTo = (hash: string): void => {
    const scrollOffset = -200;
    const elementToScrollTo = document.getElementById(hash.substr(1));
    if (elementToScrollTo) {
        const top = elementToScrollTo.getBoundingClientRect().top + window.scrollY + scrollOffset;
        window.scrollTo({ top, behavior: 'smooth' });
    }
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location, prevLocation }) => {
    // find if we've got a hash defined - if we do - scroll to it
    const hash = (window as ExtendedWindow).gatsby_router_hash;

    if (hash) {
        if (prevLocation?.pathname === location.pathname) {
            // Don't wait for transition if page is the same
            scrollTo(hash);
        } else {
            setTimeout(() => {
                scrollTo(hash);
            }, 300); // length of Framer exit transition
        }
        (window as ExtendedWindow).gatsby_router_hash = undefined;
    }
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
    (window as ExtendedWindow).dataLayer = (window as ExtendedWindow).dataLayer || [];
};

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
    const { hash } = window.location;
    if (hash) {
        setTimeout(() => {
            scrollTo(hash);
        }, 300);
    }
};

/*
 * If the pathnames are the same, and there is a router hash, don't use in-built page scroll updating
 * */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
    prevRouterProps, routerProps,
}) => !(prevRouterProps?.location?.pathname === routerProps?.location?.pathname && (window as ExtendedWindow).gatsby_router_hash);

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
    element,
}): React.ReactElement => {
    const cache = createEmotionCache();

    return (
        <CacheProvider value={cache}>
            <HelmetProvider>{element}</HelmetProvider>
        </CacheProvider>
    );
};
